import * as d3 from 'd3';
import { themesMixin } from '@/utils/themes';
import { fixedDecimals } from '@/utils/vuex';
import drawTextMixin from './d3textMixin.js';
export default {
  name: 'CoreChartGaugeModern',
  mixins: [drawTextMixin, themesMixin],
  props: {
    size: {
      type: Number,
      default: 0
    },
    value: {
      type: Number,
      default: 0
    },
    unit: {
      type: String,
      default: ''
    },
    thresholds: {
      type: Array,
      default: () => []
    },
    min: {
      type: Number,
      default: 0
    },
    max: {
      type: Number,
      default: 100
    },
    thresholdColors: {
      type: Array,
      default: () => [],
      required: false
    },
    reverse: {
      type: Boolean,
      default: false
    },
    fixTo: {
      type: Number,
      default: 2
    }
  },
  data: () => {
    return {
      bgColor: '#bdbdbd',
      svgSize: 180,
      arcRange: 270
    };
  },
  computed: {
    color() {
      return this.vuetifyColorHex();
    },
    textColor() {
      return this.$store.state.app.UiTheme ? '#ccc' : '#333';
    },
    segmentValues() {
      return [this.min, ...this.sanitizesThresholds, this.max];
    },
    sanitizesThresholds() {
      // filter out thresholds that are outside of the min/max values
      return this.thresholds.filter(item => !(item <= this.min || item >= this.max)).sort((a, b) => a - b);
    }
  },
  watch: {
    size() {
      this.draw();
    },
    value() {
      this.draw();
    },
    min() {
      this.draw();
    },
    max() {
      this.draw();
    },
    segmentValues() {
      this.draw();
    },
    color() {
      this.draw();
    },
    textColor() {
      this.draw();
    },
    reverse() {
      this.draw();
    },
    fixTo() {
      this.draw();
    }
  },
  mounted() {
    this.$nextTick(() => {
      this.draw();
    });
  },
  methods: {
    deg2Rad(deg) {
      return deg * (Math.PI / 180);
    },
    getColorArray() {
      const colors = [
      // colors when no thresholds are there
      ['#ff1744', '#fb8c00', '#fec60a', '#7cb342', '#388e3c']];
      const colorArray = this.thresholdColors.length ? this.thresholdColors : colors[0];
      return colorArray;
    },
    /* eslint-disable max-statements */
    draw() {
      const offsetTop = 14; // to center the gauge vertically as it is not a full cirlce
      const angleScale = d3.scaleLinear().range([-135, 135]);
      const value2Angle = angleScale.domain([this.min, this.max]);

      // clear existing svg content
      d3.select(this.$el).selectAll('*').remove();

      // render "N/A" if value is not a number
      if (isNaN(parseFloat(this.value))) {
        return this.drawNotAvailable();
      }
      const svg = d3.select(this.$el).attr('width', this.size || '100%').attr('height', this.size || '100%').attr('viewBox', `0 0 ${this.svgSize + 2} ${this.svgSize + 2}`).append('g').attr('transform', `translate(${this.svgSize / 2}, ${this.svgSize / 2 + offsetTop})`);
      const color = d3.scaleThreshold().domain(this.sanitizesThresholds).range(this.getColorArray(this.sanitizesThresholds));
      var arcSegmentData = this.segmentValues.map((d, i) => {
        const startAngle = angleScale(this.segmentValues[i]);
        const endAngle = angleScale(this.segmentValues[i + 1] === undefined ? this.segmentValues[i] : this.segmentValues[i + 1]);
        return {
          color: color(d),
          startAngle: this.deg2Rad(startAngle),
          endAngle: this.deg2Rad(endAngle)
        };
      });
      const radius = this.svgSize / 2;

      // arc main - background
      const arcMain = d3.arc().startAngle(this.deg2Rad(value2Angle(this.min))).innerRadius(radius - 30).outerRadius(radius - 10);
      svg.append('path').attr('d', arcMain.endAngle(this.deg2Rad(value2Angle(this.max)))).attr('fill', this.bgColor);

      // arc main - gauge value
      const arcValue = d3.arc().startAngle(this.deg2Rad(value2Angle(this.min))).innerRadius(radius - 30).outerRadius(radius - 10);

      // keep value between min and max so the arc does not break
      const valueSanitized = Math.min(Math.max(this.min, this.value), this.max);
      svg.append('path').attr('d', arcValue.endAngle(this.deg2Rad(value2Angle(valueSanitized)))).attr('fill', this.sanitizesThresholds.length < 1 ? this.color : color(valueSanitized));
      if (this.sanitizesThresholds.length < 1) {
        // draw gray solid outer ring
        const arcMain = d3.arc().startAngle(this.deg2Rad(angleScale(this.min))).innerRadius(radius - 5).outerRadius(radius);
        svg.append('path').attr('d', arcMain.endAngle(this.deg2Rad(angleScale(this.max)))).attr('fill', this.bgColor);
      } else {
        // draw colored ring per thresholds
        const outerArc = svg.append('g').attr('class', 'arc-thresholds');
        const id = Math.floor(new Date() / 1000 * Math.random());
        for (let idx = 0; idx < arcSegmentData.length - 1; idx++) {
          const d = arcSegmentData[idx];
          const arcSegment = d3.arc().startAngle(d.startAngle).innerRadius(radius - 5).outerRadius(radius);
          outerArc.append('path').attr('d', arcSegment.endAngle(d.endAngle)).attr('fill', d.color).attr('class', 'arc-threshold-value').attr('id', `${id}${idx}`);
          outerArc.append('text').style('font-size', '14px').attr('dy', '-0.25em').append('textPath').attr('xlink:href', `#${id}${idx}`).style('text-anchor', 'start').attr('startOffset', '0%').attr('fill', this.textColor).text(this.segmentValues[idx]);
        }
        const d = arcSegmentData[arcSegmentData.length - 2];
        const idx = arcSegmentData.length;
        const hiddenArc = d3.arc().startAngle(d.startAngle).innerRadius(radius - 5).outerRadius(radius);
        outerArc.append('path').attr('d', hiddenArc.endAngle(d.endAngle)).attr('fill', 'none').attr('class', 'arc-threshold-value').attr('id', `${id}${idx}`);
        outerArc.append('text').style('font-size', '14px').attr('dy', '-0.25em').append('textPath').attr('xlink:href', `#${id}${idx}`).style('text-anchor', 'end').attr('startOffset', '50%').attr('fill', this.textColor).text(this.segmentValues[idx - 1]);
      }

      // eslint-disable-next-line no-irregular-whitespace
      const space = this.unit === '%' || this.unit.startsWith('°') ? '' : ' ';
      const fixedValue = fixedDecimals(this.value, this.fixTo);
      this.drawText(this.unit ? `${fixedValue}${space}${this.unit}` : fixedValue, {
        fontColor: this.textColor,
        x: 40,
        y: 0 + offsetTop,
        width: this.svgSize - 80,
        height: this.svgSize
      });
    }
  }
  /* eslint-enable max-statements */
};